import { useEffect, useState } from "react";
import { Info, Share, Smartphone, X } from "lucide-react";
import { subscribePush } from "../lib/pwa";

// One-time welcome card for every new account: notifications are turned on
// automatically (standard, no banner asking) and the card offers
// "Get HiredToday on your phone" — the same action that always lives in the footer.
// Close it once and it never appears again; closing scrolls to the footer so
// users can see where it lives permanently.
export default function InstallAppPrompt() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [hint, setHint] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // notifications on as standard: silently subscribe when already granted,
    // ask the browser permission only once ever (never an in-app banner)
    try {
      if ("Notification" in window && "PushManager" in window) {
        if (Notification.permission === "granted") {
          subscribePush().catch(() => {});
        } else if (Notification.permission === "default" && !localStorage.getItem("ht_push_asked")) {
          localStorage.setItem("ht_push_asked", "1");
          subscribePush().catch(() => {});
        }
      }
      const installed = window.matchMedia("(display-mode: standalone)").matches || !!window.navigator.standalone;
      if (!installed && !localStorage.getItem("ht_install_dismissed")) {
        const ua = navigator.userAgent || "";
        setIsIOS(/iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
        setShow(true);
      }
    } catch (_) { /* best-effort */ }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!show) return null;

  const scrollToFooter = () => {
    try { window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); } catch (_) { /* best-effort */ }
  };

  const dismiss = () => {
    localStorage.setItem("ht_install_dismissed", "1");
    setShow(false);
    scrollToFooter();
  };

  const install = async () => {
    localStorage.setItem("ht_install_dismissed", "1");
    setShow(false);
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice.catch(() => {});
      setDeferred(null);
      scrollToFooter();
    } else {
      setHint(true);
    }
  };

  return (
    <>
      <div className="mb-6 border border-zinc-200 bg-white rounded-md px-4 py-3 flex flex-wrap items-center gap-3"
        data-testid="install-app-prompt">
        <Smartphone className="h-4 w-4 text-zinc-700 shrink-0" />
        <p className="text-sm text-zinc-700 flex-1 min-w-[200px]">
          <strong className="text-zinc-950">Get HiredToday on your phone</strong>, Android &amp; iPhone.
          One tap adds it to your home screen, no app store needed.
        </p>
        <span className="group relative inline-flex" data-testid="install-info">
          <Info className="h-4 w-4 text-zinc-400 cursor-help" />
          <span className="pointer-events-none absolute right-0 top-full z-20 mt-2 w-64 rounded-md bg-zinc-950 px-3 py-2 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            This is always at the bottom of the website too. Close this card and we'll take you there so you can see where it lives.
          </span>
        </span>
        <button onClick={install} data-testid="install-app-btn"
          className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
          Add to my phone
        </button>
        <button onClick={dismiss} data-testid="install-dismiss-btn"
          className="h-9 px-3 text-xs font-semibold text-zinc-500 border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors duration-200">
          Not now
        </button>
      </div>

      {hint && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4 bg-zinc-950/50"
          onClick={() => { setHint(false); scrollToFooter(); }} data-testid="install-hint-backdrop">
          <div className="relative bg-white rounded-lg shadow-2xl px-5 py-4 w-full max-w-xs text-center"
            onClick={(e) => e.stopPropagation()} data-testid="install-hint">
            <button type="button" onClick={() => { setHint(false); scrollToFooter(); }} aria-label="Close" data-testid="install-hint-close"
              className="absolute top-2.5 right-2.5 p-1 text-zinc-400 hover:text-zinc-700 transition-colors">
              <X className="h-4 w-4" />
            </button>
            <span className="inline-flex h-10 w-10 rounded-md bg-zinc-950 text-white items-center justify-center">
              {isIOS ? <Share className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
            </span>
            <p className="mt-3 text-sm font-semibold text-zinc-950">Almost there</p>
            {isIOS ? (
              <p className="mt-1 text-sm text-zinc-600 leading-relaxed">
                Tap <strong>Share</strong> <Share className="inline h-3.5 w-3.5 -mt-0.5 text-blue-700" /> below,
                then <strong>Add to Home Screen</strong> and tap <strong>Add</strong>.
              </p>
            ) : (
              <p className="mt-1 text-sm text-zinc-600 leading-relaxed">
                Open the browser menu <strong>⋮</strong> and tap <strong>Add to Home screen</strong>.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
